import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

export const TABLE_STATUSES = [
  'available',
  'reserved',
  'seated',
  'ordering',
  'in_service',
  'payment_pending',
  'cleaning',
  'out_of_service',
] as const;
export const tableStatusSchema = z.enum(TABLE_STATUSES);
export type TableStatus = z.infer<typeof tableStatusSchema>;

export const floorSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sort_order: z.number().int(),
});
export type Floor = z.infer<typeof floorSchema>;

export const tableSessionSummarySchema = z.object({
  id: uuidSchema,
  status: z.enum(['open', 'payment_pending', 'closed']),
  guest_count: z.number().int(),
  server_user_id: uuidSchema.nullable(),
  server_name: z.string().nullable(),
  opened_at: isoTimestampSchema,
  order_id: uuidSchema.nullable(),
  bill_id: uuidSchema.nullable(),
  /** Running total of submitted (non-cancelled) items, in branch currency minor units. */
  ordered_total_minor: z.number().int(),
});
export type TableSessionSummary = z.infer<typeof tableSessionSummarySchema>;

export const diningTableSchema = z.object({
  id: uuidSchema,
  floor_id: uuidSchema,
  name: z.string(),
  capacity: z.number().int(),
  status: tableStatusSchema,
  active: z.boolean(),
  sort_order: z.number().int(),
  version: z.number().int(),
  session: tableSessionSummarySchema.nullable(),
});
export type DiningTable = z.infer<typeof diningTableSchema>;

export const floorPlanResponseSchema = z.object({
  floors: z.array(floorSchema),
  tables: z.array(diningTableSchema),
});
export type FloorPlan = z.infer<typeof floorPlanResponseSchema>;

/*
 * Update schemas are built from field shapes WITHOUT `.default()`: in zod 4 `.partial()` keeps
 * defaults, which would silently reset omitted fields on PATCH.
 */
const floorFields = {
  name: z.string().trim().min(1).max(60),
  sort_order: z.number().int().min(0),
};
export const createFloorRequestSchema = z.object({
  ...floorFields,
  sort_order: floorFields.sort_order.default(0),
});
export const updateFloorRequestSchema = z.object(floorFields).partial();

export const createTableRequestSchema = z.object({
  floor_id: uuidSchema,
  name: z.string().trim().min(1).max(20),
  capacity: z.number().int().min(1).max(50),
  sort_order: z.number().int().min(0).default(0),
});
export const updateTableRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  floor_id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(20).optional(),
  capacity: z.number().int().min(1).max(50).optional(),
  sort_order: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
  /** Only manual transitions: available <-> out_of_service, cleaning -> available. */
  status: z.enum(['available', 'out_of_service']).optional(),
});

export const openTableSessionRequestSchema = z.object({
  table_id: uuidSchema,
  guest_count: z.number().int().min(1).max(50),
  /** Defaults to the caller. */
  server_user_id: uuidSchema.optional(),
});

export const tableSessionSchema = tableSessionSummarySchema.extend({
  table_id: uuidSchema,
  table_name: z.string(),
  closed_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
});
export type TableSession = z.infer<typeof tableSessionSchema>;

/** Guest QR code of one table (ADR-0023). Needs `floor.manage`: the token opens the table's bill. */
export const tableQrCodeSchema = z.object({
  table_id: uuidSchema,
  table_name: z.string(),
  floor_id: uuidSchema,
  floor_name: z.string(),
  active: z.boolean(),
  token: z.string(),
  /** Same-origin path of the guest menu; the client prefixes its own origin. */
  path: z.string(),
});
export type TableQrCode = z.infer<typeof tableQrCodeSchema>;
export const tableQrCodeListResponseSchema = z.object({ items: z.array(tableQrCodeSchema) });
